import React from "react";
import ReactDOM from "react-dom/client";

// import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { Provider } from "react-redux";
import { store } from "./store/redux/store";
import { pdfjs } from "react-pdf";
import { LanguageProvider } from "./store/context/language-context";
import { ThemeProvider } from "./store/context/theme-context";

// import { BrowserRouter } from 'react-router-dom';
import { initI18n } from "./i18n";
import { FeatureProvider } from "./store/context/feature-context";
import { loadFeatureToggles } from "./utils/helper/feature-toggle";
import { loadPermissionFlags } from "./utils/helper/permission-toggle";
import { PermissionProvider } from "./store/context/permission-context";
import { getTenant } from "./shared/config/tenantConfiguration";
import { UserPreferenceProvider } from "./store/context/user-preference-context";
import { postAuthenticatedRequest } from "./utils/api/requests/request";
import { UserPreferenceEnum } from "./shared/constants/constants";
import { getSessionData } from "./utils/interceptors/localStorageService";

const tenant = getTenant();
import(`./assets/tenants/${tenant?.cssName}/index.css`);

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const root = ReactDOM.createRoot(document.getElementById("root"));

const preloadUserPrefs = async (keys) => {
  try {
    const results = await Promise.all(
      keys.map((key) =>
        postAuthenticatedRequest("RetrieveUserPreferenceQuery", { key })
      )
    );
    const merged = results.reduce((acc, resp) => {
      if (resp?.outData?.isSuccessful && resp?.outData?.data) {
        Object.assign(acc, resp.outData.data);
      }
      return acc;
    }, {});

    // Apply theme directly without persisting to localStorage
    const theme = merged?.[UserPreferenceEnum.THEME];
    if (theme === "dark") {
      document.body.classList.add("dark");
    } else {
      document.body.classList.remove("dark");
    }

    return merged;
  } catch (_) {
    // ignore; render continues with saved defaults
    return {};
  }
};

const session = getSessionData();
let initialTheme = "light";
let initialLanguage = "en";
let initialUserPreferences = {};
const preloadPromise = session
  ? preloadUserPrefs([
      UserPreferenceEnum.LANGUAGE,
      UserPreferenceEnum.THEME,
    ]).then((prefs) => {
      if (prefs?.[UserPreferenceEnum.THEME]) {
        initialTheme = prefs[UserPreferenceEnum.THEME];
      }
      if (prefs?.[UserPreferenceEnum.LANGUAGE]) {
        initialLanguage = prefs[UserPreferenceEnum.LANGUAGE];
      }
      // Store all preloaded preferences for the UserPreferenceProvider
      initialUserPreferences = prefs || {};
    })
  : Promise.resolve();

// Wait for preferences first, then pass language into i18n init and prefetch feature toggles
preloadPromise
  .then(async () => {
    await initI18n(initialLanguage);
    const features = await loadFeatureToggles();
    const permissions = await loadPermissionFlags();
    console.log("permissions and features", { permissions, features });
    return { features, permissions };
  })
  .catch(() => {})
  .then(({ features } = {}) => {
    root.render(
      <React.StrictMode>
        <Provider store={store}>
          {" "}
          <UserPreferenceProvider initialPreferences={initialUserPreferences}>
            <ThemeProvider initialTheme={initialTheme}>
              <LanguageProvider initialLanguage={initialLanguage}>
                <FeatureProvider initialFeatures={features}>
                  <PermissionProvider>
                    <App />
                  </PermissionProvider>
                </FeatureProvider>
              </LanguageProvider>
            </ThemeProvider>
          </UserPreferenceProvider>
        </Provider>
      </React.StrictMode>
    );
  });

reportWebVitals();
